import React, { useState, useEffect, Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import LoginForm from "./components/LoginForm";
import PracticeDashboard from "./components/PracticeDashboard";
import { OfficeGate } from "./components/offices/OfficeGate";
import TrialExpiredGate from "./components/TrialExpiredGate";
import TrialEndingBanner from "./components/TrialEndingBanner";
import { BaaGate } from "./components/baa/BaaGate";
import { SetPasswordScreen } from "./components/SetPasswordScreen";
import { WelcomeVideoModal } from "./components/WelcomeVideoModal";
import { WelcomeChecklist } from "./components/onboarding/WelcomeChecklist";
import { RequirePermission } from "./components/RequirePermission";
import AdminLogin from "./components/AdminLogin";
import PracticeLogin from "./components/PracticeLogin";
import { SmileAuthProvider } from "./pages/SmileApp/SmileAuthContext";
import { Toaster } from "./components/ui/toaster";
import LoadingSpinner from "./components/LoadingSpinner";
import JobsLandingPage from "./pages/JobsLandingPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import PractComTermsPage from "./pages/PractComTermsPage";
import PractComPrivacyPage from "./pages/PractComPrivacyPage";
import BetaPage from "./pages/BetaPage";
import JobsSignupPage from "./pages/JobsSignupPage";
import JobsLoginPage from "./pages/JobsLoginPage";
import JobsForgotPasswordPage from "./pages/JobsForgotPasswordPage";
import JobsResetPasswordPage from "./pages/JobsResetPasswordPage";
import JobsDashboardPage from "./pages/JobsDashboardPage";
import PractComUpgradePage from "./pages/PractComUpgradePage";
const LandingPage = lazy(() => import("./pages/LandingPage"));

// Full-viewport iframe wrapper that serves the OnSpace landing build
// while keeping the parent URL (/landing) in the address bar.
function OnspaceEmbed() {
  return (
    <iframe
      title="PractCom Landing"
      src={`/onspace/index.html${window.location.search}`}
      data-testid="onspace-landing-iframe"
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        border: 'none',
        margin: 0,
        padding: 0,
        zIndex: 0,
      }}
      allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
    />
  );
}
import { DrDanWidget } from "./components/drdan/DrDanWidget";
import { JobseekerAuthProvider } from "./contexts/JobseekerAuthContext";
// Lazy-loaded pages with prefetch — downloads in background during idle time
const HomePage = lazy(() => import(/* webpackPrefetch: true */ "./pages/HomePage"));
const SpecialtyPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/SpecialtyPage"));
const ProcedurePage = lazy(() => import(/* webpackPrefetch: true */ "./pages/ProcedurePage"));
const RegistrationPage = lazy(() => import("./pages/RegistrationPage"));
const WelcomePage = lazy(() => import("./pages/WelcomePage"));
const WelcomeMessagePage = lazy(() => import("./pages/WelcomeMessagePage"));
const PracticeSettingsPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/PracticeSettingsPage"));
const AddPatientPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/AddPatientPage"));
const AssignProcedurePage = lazy(() => import(/* webpackPrefetch: true */ "./pages/AssignProcedurePage"));
const AssignMedCardPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/AssignMedCardPage"));
const MedCardLibraryPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/MedCardLibraryPage"));
const ManagementFormsLibraryPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/ManagementFormsLibraryPage"));
const ProblemsEmergenciesLibraryPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/ProblemsEmergenciesLibraryPage"));
const AssignProblemsEmergenciesPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/AssignProblemsEmergenciesPage"));
const ProblemDetailsPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/ProblemDetailsPage"));
const AssignInformedConsentPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/AssignInformedConsentPage"));
const ProcedureDetailsPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/ProcedureDetailsPage"));
const MedCardsDetailsPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/MedCardsDetailsPage"));
const EditProcedurePage = lazy(() => import(/* webpackPrefetch: true */ "./pages/EditProcedurePage"));
const EditPatientPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/EditPatientPage"));
const PatientManagementPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/PatientManagementPage"));
const RequestProcedurePage = lazy(() => import(/* webpackPrefetch: true */ "./pages/RequestProcedurePage"));
const PracticeLibraryPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/PracticeLibraryPage"));
const WebsiteChatbotPage = lazy(() => import("./pages/WebsiteChatbotPage"));
const NewsletterPage = lazy(() => import("./pages/newsletter/NewsletterPage"));
const NewsletterSchedulerPage = lazy(() => import("./pages/newsletter/NewsletterSchedulerPage"));
const MessagesPage = lazy(() => import("./pages/MessagesPage"));
const ReviewsPage = lazy(() => import("./pages/ReviewsPage"));
const PublicReviewPage = lazy(() => import("./pages/PublicReviewPage"));
const PatientEcardsPage = lazy(() => import("./pages/PatientEcardsPage"));
const PlansBillingPage = lazy(() => import("./pages/PlansBillingPage"));
const PaymentSuccessPage = lazy(() => import("./pages/PaymentSuccessPage"));
const PaymentCancelPage = lazy(() => import("./pages/PaymentCancelPage"));
const DentistManagementPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/DentistManagementPage"));
const StaffManagementPage = lazy(() => import("./pages/StaffManagementPage"));
const OfficesPage = lazy(() => import("./pages/OfficesPage"));
const OfficeSettingsPage = lazy(() => import("./pages/OfficeSettingsPage"));
const ReferralDentistsManagementPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/ReferralDentistsManagementPage"));
const PatientLoginPage = lazy(() => import("./pages/PatientLoginPage"));
const FollowUpStatsPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/FollowUpStatsPage"));
const PatientProcedureView = lazy(() => import(/* webpackPrefetch: true */ "./pages/PatientProcedureView"));
const PatientPortalPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/PatientPortalPage"));
const ProcedureViewPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/ProcedureViewPage"));
const PracticeProcedureView = lazy(() => import(/* webpackPrefetch: true */ "./pages/PracticeProcedureView"));
const PracticeProcedureEditPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/PracticeProcedureEditPage"));
const InformedConsentLibraryPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/InformedConsentLibraryPage"));
const AssignComplianceFormPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/AssignComplianceFormPage"));
const FollowUpSettingsPage = lazy(() => import("./pages/FollowUpSettingsPage"));
const FollowUpReplyPage = lazy(() => import("./pages/FollowUpReplyPage"));
const EditInformedConsentPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/EditInformedConsentPage"));
const ConsentSigningPage = lazy(() => import("./pages/ConsentSigningPage"));
const BundleSigningPage = lazy(() => import("./pages/BundleSigningPage"));
const ReferralManagementPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/ReferralManagementPage"));
const InboundReferralsPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/InboundReferralsPage"));
const ReferralAnalyticsPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/ReferralAnalyticsPage"));
const AskDanPage = lazy(() => import("./pages/AskDanPage"));
const AdminDisclaimerReset = lazy(() => import("./pages/AdminDisclaimerReset"));
const ToothGuidesPage = lazy(() => import(/* webpackPrefetch: true */ "./pages/ToothGuidesPage"));
const PatientDashboard = lazy(() => import(/* webpackPrefetch: true */ "./components/PatientDashboard"));
const PasswordReset = lazy(() => import("./components/PasswordReset"));
const DatabaseSeeder = lazy(() => import("./components/DatabaseSeeder"));
const FeedbackPage = lazy(() => import("./pages/FeedbackPage"));
const FounderSharePage = lazy(() => import("./pages/FounderSharePage"));
const FreeTemplatesPage = lazy(() => import("./pages/FreeTemplatesPage"));
const SmileDesignAI = lazy(() => import("./pages/SmileDesignAI"));
const PublicSmileDesign = lazy(() => import("./pages/PublicSmileDesign"));
const PublicDocumentView = lazy(() => import("./pages/PublicDocumentView"));
const SmileDesignReceptionSign = lazy(() => import("./components/SmileDesignReceptionSign"));
const SmileAppHome = lazy(() => import("./pages/SmileApp/SmileAppHome"));
const SmileAppLogin = lazy(() => import("./pages/SmileApp/SmileAppLogin"));
const SmileAppRegister = lazy(() => import("./pages/SmileApp/SmileAppRegister"));
const SmileAppAdmin = lazy(() => import("./pages/SmileApp/SmileAppAdmin"));
const SmileAppTransform = lazy(() => import("./pages/SmileApp/SmileAppTransform"));
const SmileAppPatient = lazy(() => import("./pages/SmileApp/SmileAppPatient"));
const SmileAppDashboard = lazy(() => import("./pages/SmileApp/SmileAppDashboard"));
const PracticeApplicantInboxPage = lazy(() => import("./pages/PracticeApplicantInboxPage"));

// Short code resolver - resolves /p/:code to /portal/:token
function ShortCodeResolver() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [error, setError] = React.useState(null);
  
  React.useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/resolve/${code}`)
      .then(r => r.json())
      .then(data => {
        if (data.success && data.token) {
          navigate(`/portal/${data.token}`, { replace: true });
        } else {
          setError('Invalid portal link');
        }
      })
      .catch(() => setError('Unable to connect. Please try again.'));
  }, [code, navigate]);
  
  if (error) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb' }}>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#374151' }}>Access Denied</h2>
          <p style={{ color: '#6b7280', marginTop: '0.5rem' }}>{error}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: '48px', height: '48px', border: '4px solid #3b82f6', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }} />
        <p style={{ marginTop: '1rem', color: '#6b7280' }}>Loading your portal...</p>
      </div>
    </div>
  );
}


// Redirect root to /smile-app when accessed from smiledesignai.org
const SmileAppRedirect = ({ children }) => {
  const host = window.location.hostname;
  if (host.includes('smiledesignai')) return <Navigate to="/smile-app" replace />;
  return children;
};

// Main App Content Component
const AppContent = () => {
  const { user, loading, isAuthenticated, isPracticeStaff, isPatient, logout } = useAuth();
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'register'

  // If ?fresh=true in URL, clear all auth tokens so user sees the login form
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('fresh') === 'true') {
      localStorage.removeItem('dentalToken');
      localStorage.removeItem('dentalUser');
      localStorage.removeItem('adminToken');
      if (logout) logout();
      // Remove ?fresh=true from URL to prevent loop
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);
  
  // Navigation state for public library
  const [currentView, setCurrentView] = useState('home');
  const [selectedSpecialty, setSelectedSpecialty] = useState(null);
  const [selectedProcedure, setSelectedProcedure] = useState(null);

  const handleSelectSpecialty = (specialtyId) => {
    setSelectedSpecialty(specialtyId);
    setCurrentView('specialty');
  };

  const handleSelectProcedure = (procedureId) => {
    setSelectedProcedure(procedureId);
    setCurrentView('procedure');
  };

  const handleBackToHome = () => {
    setCurrentView('home');
    setSelectedSpecialty(null);
    setSelectedProcedure(null);
  };

  const handleBackToSpecialty = () => {
    setCurrentView('specialty');
    setSelectedProcedure(null);
  };

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  // Lazy loading fallback
  const PageLoader = (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
      <LoadingSpinner size="lg" />
    </div>
  );

  // If user is authenticated
  if (isAuthenticated()) {
    // Practice staff/admin see the dashboard
    if (isPracticeStaff()) {
      if (user?.mustChangePassword) return <SetPasswordScreen />;
      return (
        <TrialExpiredGate>
          <TrialEndingBanner />
          <WelcomeVideoModal />
          <WelcomeChecklist />
          <OfficeGate><PracticeDashboard /></OfficeGate>
        </TrialExpiredGate>
      );
    }
    
    // Patients see the public library (for now)
    if (isPatient()) {
      return (
        <Suspense fallback={PageLoader}>
          {currentView === 'home' && (
            <HomePage 
              onSelectSpecialty={handleSelectSpecialty}
              onSelectProcedure={handleSelectProcedure}
            />
          )}
          {currentView === 'specialty' && (
            <SpecialtyPage 
              specialtyId={selectedSpecialty}
              onSelectProcedure={handleSelectProcedure}
              onBackToHome={handleBackToHome}
            />
          )}
          {currentView === 'procedure' && (
            <ProcedurePage 
              procedureId={selectedProcedure}
              onBackToHome={handleBackToHome}
              onBackToSpecialty={handleBackToSpecialty}
            />
          )}
        </Suspense>
      );
    }
    
    // Fallback: If authenticated but role not recognized, default to practice dashboard
    // This handles edge cases like after password reset or role mismatches
    console.warn('User authenticated but role not recognized:', user?.role);
    return (
      <TrialExpiredGate>
        <TrialEndingBanner />
        <PracticeDashboard />
      </TrialExpiredGate>
    );
  }

  // Not authenticated - show original login form
  return <LoginForm />;
};

// ── Landing / funnel analytics: fires a pageview on every route change ──
function AnalyticsTracker() {
  const location = useLocation();
  React.useEffect(() => {
    let cancelled = false;
    import("./utils/analytics").then(({ installAnalytics, trackPageview }) => {
      if (cancelled) return;
      installAnalytics();
      trackPageview(location.pathname + location.search);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [location.pathname, location.search]);
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AnalyticsTracker />
        <BaaGate />
        <Suspense fallback={
          <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
            <LoadingSpinner size="lg" />
          </div>
        }>
        <Routes>
          {/* Admin Route - Must be EXACT path, not wildcard */}
          <Route path="/admin" element={<AdminLogin />} />
          
          {/* Database Seeder Tool - Admin only */}
          <Route path="/seed-database" element={<DatabaseSeeder />} />
          
          {/* Password Reset Route - No Auth Required */}
          <Route path="/reset-password" element={<PasswordReset />} />
          
          {/* Testing route to verify password reset works */}
          <Route path="/test-reset" element={<PasswordReset />} />
          
          {/* Feedback Page - No Auth Required */}
          <Route path="/feedback" element={<FeedbackPage />} />

          {/* Founder Share / Testimonial Capture - No Auth Required */}
          <Route path="/founder/share" element={<FounderSharePage />} />

          {/* Free 12-Template Lead Magnet PDF - No Auth Required */}
          <Route path="/free-templates" element={<FreeTemplatesPage />} />

          {/* PractCom Upgrade landing — target of the 14-day Smile Design AI upsell email */}
          <Route path="/practcom-upgrade" element={<PractComUpgradePage />} />

          {/* Public landing page — full OnSpace build served inside a full-viewport iframe */}
          <Route path="/landing" element={<OnspaceEmbed />} />
          <Route path="/welcome" element={<WelcomePage />} />
          <Route path="/welcome-message" element={<WelcomeMessagePage />} />
          <Route path="/thank-you" element={<WelcomePage />} />

          {/* DentalStaff by PractCom - Public Job Portal (No Auth Required) */}
          <Route path="/jobs" element={<JobsLandingPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/practcom/privacy" element={<PractComPrivacyPage />} />
          <Route path="/practcom/terms" element={<PractComTermsPage />} />
          <Route path="/beta" element={<BetaPage />} />

          {/* DentalStaff — Jobseeker Auth Flows (isolated provider) */}
          <Route path="/jobs/signup"           element={<JobseekerAuthProvider><JobsSignupPage /></JobseekerAuthProvider>} />
          <Route path="/jobs/login"            element={<JobseekerAuthProvider><JobsLoginPage /></JobseekerAuthProvider>} />
          <Route path="/jobs/forgot-password"  element={<JobsForgotPasswordPage />} />
          <Route path="/jobs/reset-password"   element={<JobsResetPasswordPage />} />
          <Route path="/jobs/dashboard"        element={<JobseekerAuthProvider><JobsDashboardPage /></JobseekerAuthProvider>} />

          {/* PractCom — Practice-side Applicant Inbox (uses existing practice JWT) */}
          <Route path="/practice/applicants"   element={<AuthProvider><RequirePermission perm="settings"><PracticeApplicantInboxPage /></RequirePermission></AuthProvider>} />
          
          {/* Public Document View - For SMS links - No Auth Required */}
          <Route path="/public-view/:documentType/:documentId" element={<PublicDocumentView />} />
          <Route path="/consent-sign/:token" element={<ConsentSigningPage />} />
          <Route path="/consent-bundle/:token" element={<BundleSigningPage />} />
          
          {/* Public SmileDesign AI - Patient-facing, No Auth Required */}
          <Route path="/public-smile-design" element={<PublicSmileDesign />} />
          
          {/* SmileDesign Reception Sign - For testing/preview */}
          <Route path="/smile-design-sign" element={<SmileDesignReceptionSign />} />
          
          {/* SmileDesign AI - Requires Auth */}
          <Route path="/smile-design" element={
            <AuthProvider>
              <RequirePermission perm="clinical"><SmileDesignAI /></RequirePermission>
            </AuthProvider>
          } />
          
          {/* Practice Login Route - For individual practices */}
          <Route path="/practice-portal" element={
            <AuthProvider>
              <PracticeLogin />
            </AuthProvider>
          } />
          
          {/* All other specific routes within AuthProvider */}
          <Route path="/register" element={
            <AuthProvider>
              <RegistrationPage />
            </AuthProvider>
          } />
          <Route path="/practice-settings" element={
            <AuthProvider>
              <RequirePermission perm="settings"><PracticeSettingsPage /></RequirePermission>
            </AuthProvider>
          } />
          <Route path="/add-patient" element={
            <AuthProvider>
              <AddPatientPage />
            </AuthProvider>
          } />
          <Route path="/assign-procedure" element={
            <AuthProvider>
              <AssignProcedurePage />
            </AuthProvider>
          } />
          <Route path="/med-cards/assign" element={
            <AuthProvider>
              <AssignMedCardPage />
            </AuthProvider>
          } />
          <Route path="/med-cards/library" element={
            <AuthProvider>
              <MedCardLibraryPage />
            </AuthProvider>
          } />
          <Route path="/management-forms/library" element={
            <AuthProvider>
              <ManagementFormsLibraryPage />
            </AuthProvider>
          } />
          <Route path="/problems-emergencies/library" element={
            <AuthProvider>
              <ProblemsEmergenciesLibraryPage />
            </AuthProvider>
          } />
          <Route path="/assign-problems-emergencies" element={
            <AuthProvider>
              <AssignProblemsEmergenciesPage />
            </AuthProvider>
          } />
          <Route path="/problem-details/:assignmentId" element={
            <AuthProvider>
              <ProblemDetailsPage />
            </AuthProvider>
          } />
          <Route path="/assign-informed-consent" element={
            <AuthProvider>
              <AssignInformedConsentPage />
            </AuthProvider>
          } />
          <Route path="/request-procedure" element={
            <AuthProvider>
              <RequestProcedurePage />
            </AuthProvider>
          } />
          <Route path="/patient-management" element={
            <AuthProvider>
              <PatientManagementPage />
            </AuthProvider>
          } />
          <Route path="/edit-patient" element={
            <AuthProvider>
              <EditPatientPage />
            </AuthProvider>
          } />
          <Route path="/procedure-details/:procedureId" element={
            <AuthProvider>
              <ProcedureDetailsPage />
            </AuthProvider>
          } />
          <Route path="/med-cards-details/:assignmentId" element={
            <AuthProvider>
              <MedCardsDetailsPage />
            </AuthProvider>
          } />
          <Route path="/procedure-view/:procedureId" element={
            <AuthProvider>
              <ProcedureViewPage />
            </AuthProvider>
          } />
          <Route path="/practice-procedure/:procedureId" element={
            <AuthProvider>
              <PracticeProcedureView />
            </AuthProvider>
          } />
          <Route path="/edit-procedure/:procedureId" element={
            <AuthProvider>
              <EditProcedurePage />
            </AuthProvider>
          } />
          <Route path="/procedure-library" element={
            <AuthProvider>
              <PracticeLibraryPage />
            </AuthProvider>
          } />
          <Route path="/newsletter" element={<AuthProvider><NewsletterPage /></AuthProvider>} />
          <Route path="/newsletter/scheduler" element={<AuthProvider><NewsletterSchedulerPage /></AuthProvider>} />
          <Route path="/reviews" element={<AuthProvider><ReviewsPage /></AuthProvider>} />
          <Route path="/r/:token" element={<PublicReviewPage />} />
          <Route path="/messages" element={
            <AuthProvider>
              <MessagesPage />
            </AuthProvider>
          } />
          <Route path="/website-chatbot" element={
            <AuthProvider>
              <RequirePermission perm="settings"><WebsiteChatbotPage /></RequirePermission>
            </AuthProvider>
          } />
          <Route path="/patient-ecards" element={
            <AuthProvider>
              <PatientEcardsPage />
            </AuthProvider>
          } />
          <Route path="/plans-billing" element={
            <AuthProvider>
              <RequirePermission perm="billing"><PlansBillingPage /></RequirePermission>
            </AuthProvider>
          } />
          <Route path="/payment/success" element={<PaymentSuccessPage />} />
          <Route path="/payment/cancel" element={<PaymentCancelPage />} />
          <Route path="/practice/procedures" element={
            <AuthProvider>
              <PracticeLibraryPage />
            </AuthProvider>
          } />
          <Route path="/practice/procedures/:procedureId/edit" element={
            <AuthProvider>
              <PracticeProcedureEditPage />
            </AuthProvider>
          } />
          <Route path="/informed-consent-library" element={
            <AuthProvider>
              <InformedConsentLibraryPage />
            </AuthProvider>
          } />

          <Route path="/compliance-forms-library" element={
            <AuthProvider>
              <InformedConsentLibraryPage />
            </AuthProvider>
          } />

          <Route path="/assign-compliance-form" element={
            <AuthProvider>
              <AssignComplianceFormPage />
            </AuthProvider>
          } />

          <Route path="/follow-up-settings" element={
            <AuthProvider>
              <RequirePermission perm="settings"><FollowUpSettingsPage /></RequirePermission>
            </AuthProvider>
          } />

          <Route path="/follow-up-reply/:token" element={<FollowUpReplyPage />} />
          
          <Route path="/edit-informed-consent/:consentId" element={
            <AuthProvider>
              <EditInformedConsentPage />
            </AuthProvider>
          } />

          <Route path="/referral-management" element={
            <AuthProvider>
              <ReferralManagementPage />
            </AuthProvider>
          } />

          <Route path="/inbound-referrals" element={
            <AuthProvider>
              <InboundReferralsPage />
            </AuthProvider>
          } />

          <Route path="/referral-analytics" element={
            <AuthProvider>
              <ReferralAnalyticsPage />
            </AuthProvider>
          } />

          <Route path="/ask-dan" element={
            <AuthProvider>
              <AskDanPage />
            </AuthProvider>
          } />
          
          <Route path="/tooth-guides" element={
            <AuthProvider>
              <ToothGuidesPage />
            </AuthProvider>
          } />
          
          <Route path="/admin/reset-disclaimer" element={
            <AuthProvider>
              <AdminDisclaimerReset />
            </AuthProvider>
          } />
          
          <Route path="/offices/:officeId/settings" element={
            <AuthProvider>
              <OfficeSettingsPage />
            </AuthProvider>
          } />

          <Route path="/offices" element={
            <AuthProvider>
              <OfficesPage />
            </AuthProvider>
          } />

          <Route path="/staff-management" element={
            <AuthProvider>
              <RequirePermission perm="staff"><StaffManagementPage /></RequirePermission>
            </AuthProvider>
          } />

          <Route path="/dentist-management" element={
            <AuthProvider>
              <RequirePermission perm="settings"><DentistManagementPage /></RequirePermission>
            </AuthProvider>
          } />
          
          <Route path="/referral-dentists" element={
            <AuthProvider>
              <ReferralDentistsManagementPage />
            </AuthProvider>
          } />
          
          {/* Practice Notes Route - Main Login */}
          <Route path="/practice-notes" element={
            <AuthProvider>
              <AppContent />
            </AuthProvider>
          } />
          
          {/* Patient Routes */}
          <Route path="/patient/login" element={
            <AuthProvider>
              <PatientLoginPage />
            </AuthProvider>
          } />
          <Route path="/patient/dashboard" element={
            <AuthProvider>
              <PatientDashboard />
            </AuthProvider>
          } />
          <Route path="/patient/procedure/:assignmentId" element={
            <AuthProvider>
              <PatientProcedureView />
            </AuthProvider>
          } />
          <Route path="/portal/:token" element={
            <PatientPortalPage />
          } />
          <Route path="/p/:code" element={
            <ShortCodeResolver />
          } />
          
          <Route path="/dashboard" element={
            <AuthProvider>
              <AppContent />
            </AuthProvider>
          } />
          <Route path="/practice" element={
            <AuthProvider>
              <AppContent />
            </AuthProvider>
          } />
          {/* ======== Standalone Smile Design AI App ======== */}
          <Route path="/smile-app" element={<SmileAuthProvider><SmileAppHome /></SmileAuthProvider>} />
          <Route path="/smile-app/login" element={<SmileAuthProvider><SmileAppLogin /></SmileAuthProvider>} />
          <Route path="/smile-app/register" element={<SmileAuthProvider><SmileAppRegister /></SmileAuthProvider>} />
          <Route path="/smile-app/admin" element={<SmileAuthProvider><SmileAppAdmin /></SmileAuthProvider>} />
          <Route path="/smile-app/transform" element={<SmileAuthProvider><SmileAppTransform /></SmileAuthProvider>} />
          <Route path="/smile-app/dashboard" element={<SmileAuthProvider><SmileAppDashboard /></SmileAuthProvider>} />
          <Route path="/smile-app/p/:practiceId" element={<SmileAppPatient />} />
          
          <Route path="/" element={
            <SmileAppRedirect>
              <AuthProvider>
                <AppContent />
              </AuthProvider>
            </SmileAppRedirect>
          } />
          
          {/* Catch-all route - MUST BE LAST */}
          <Route path="*" element={
            <SmileAppRedirect>
              <AuthProvider>
                <AppContent />
              </AuthProvider>
            </SmileAppRedirect>
          } />
        </Routes>
        <DrDanWidget />
        </Suspense>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;// v1763918679
